import { Box, RelativeTime, Stack, Text } from "@sembark-travel/ui-base"
import { useState } from "react"
import type {
	TWAQuickSend,
	TWAQuickSendMessage,
} from "../../WhatsAppQuickSends/store"
import { WhatsAppImageDialog } from "./ImageDialog"
import { WhatsAppMessageBubble } from "./MessageBubble"

/**
 * A quick send's messages drawn as the conversation will show them once
 * sent: our bubbles, in order, files as the customer sees them and captions
 * beneath.
 */
export function WhatsAppQuickSendPreview({
	quickSend,
}: {
	quickSend: Pick<TWAQuickSend, "messages">
}) {
	const messages = quickSend.messages ?? []

	return (
		<Box bgColor="subtle" rounded="md" padding="3">
			{messages.length ? (
				<Stack gap="2">
					{messages.map((message) => (
						<WhatsAppMessageBubble key={message.id} inbound={false}>
							<QuickSendMessageContent message={message} />
						</WhatsAppMessageBubble>
					))}
				</Stack>
			) : (
				<Text color="muted">This quick send has no messages.</Text>
			)}
		</Box>
	)
}

/** How often agents have sent it, and when one last did. */
export function QuickSendUsage({
	quickSend,
}: {
	quickSend: Pick<TWAQuickSend, "use_count" | "last_used_at">
}) {
	if (!quickSend.use_count) {
		return (
			<Text as="span" fontSize="sm" color="muted">
				Never sent
			</Text>
		)
	}

	return (
		<Text as="span" fontSize="sm" color="muted">
			Sent {quickSend.use_count} {quickSend.use_count === 1 ? "time" : "times"}
			{quickSend.last_used_at ? (
				<>
					, last <RelativeTime timestamp={quickSend.last_used_at} />
				</>
			) : null}
		</Text>
	)
}

/**
 * The same shapes the conversation renders a sent message's media with, so
 * the preview and the thread look alike.
 */
function QuickSendMessageContent({
	message,
}: {
	message: TWAQuickSendMessage
}) {
	const { file } = message
	const [isExpanded, setIsExpanded] = useState(false)

	if (message.type === "flow") {
		return <QuickSendFlowContent message={message} />
	}

	return (
		<>
			{message.type === "image" && file ? (
				<>
					<button
						type="button"
						aria-label={`View ${file.file_name} full size`}
						onClick={() => setIsExpanded(true)}
						style={{
							display: "block",
							padding: 0,
							border: 0,
							background: "none",
							cursor: "zoom-in",
						}}
					>
						<img
							src={file.url}
							alt={file.file_name}
							style={{ maxWidth: "100%", borderRadius: 6, display: "block" }}
						/>
					</button>
					<WhatsAppImageDialog
						src={isExpanded ? file.url : null}
						alt={file.file_name}
						captionHtml={message.body_html ?? null}
						onClose={() => setIsExpanded(false)}
					/>
				</>
			) : null}
			{message.type === "video" && file ? (
				<video
					src={file.url}
					controls
					preload="metadata"
					style={{ maxWidth: "100%", borderRadius: 6, display: "block" }}
				/>
			) : null}
			{message.type === "document" && file ? (
				<a
					href={file.url}
					target="_blank"
					rel="noopener noreferrer"
					style={{
						display: "inline-flex",
						gap: 6,
						alignItems: "center",
						padding: "8px 12px",
						border: "1px solid",
						borderRadius: 6,
						textDecoration: "none",
					}}
				>
					<span role="img" aria-label="Document">
						📄
					</span>{" "}
					{file.file_name}
				</a>
			) : null}
			{message.type !== "text" && !file ? (
				<Text color="danger" fontSize="sm">
					This message has lost its file, so a send stops here.
				</Text>
			) : null}
			<QuickSendBody html={message.body_html} />
		</>
	)
}

/**
 * The text of a message, a caption or the text above a form's button, with
 * WhatsApp's formatting applied as the conversation shows it once sent.
 */
function QuickSendBody({ html }: { html?: string | null }) {
	if (!html) return null

	return (
		<Text
			as="div"
			whiteSpace="preserveLine"
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	)
}

/**
 * A form as the customer sees it: the text, then the button that opens the
 * form, as a sent one is drawn in the conversation.
 */
function QuickSendFlowContent({ message }: { message: TWAQuickSendMessage }) {
	const { flow } = message

	return (
		<Stack gap="2">
			<QuickSendBody html={message.body_html} />
			<Box
				borderTopWidth="1"
				paddingTop="2"
				textAlign="center"
				color="accent"
				fontWeight="semibold"
			>
				<span role="img" aria-label="Form">
					📝
				</span>{" "}
				{message.flow_cta}
			</Box>
			{!flow || flow.deleted ? (
				<Text color="danger" fontSize="sm">
					This form has been removed, so this quick send cannot be sent.
				</Text>
			) : (
				<Text color="muted" fontSize="sm">
					Opens “{flow.name}”
					{flow.status !== "PUBLISHED"
						? ` · ${flow.status.toLowerCase()}, so WhatsApp may refuse it`
						: ""}
				</Text>
			)}
		</Stack>
	)
}
